import { AlertCircle, Save } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";

import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  accountDigits,
  formatAccountGroups,
  groupedAccountMaxLength,
  isValidAccount,
} from "../services/depositAccounts.service";
import type {
  DepositAccountRow,
  DepositAccountsCorridor,
  RegisterDepositAccountOutcome,
} from "../types/depositAccounts.types";

export interface RegisterDepositAccountDialogProps {
  corridor: DepositAccountsCorridor;
  open: boolean;
  onClose: () => void;
  /** The org selected on the page — the mapping is registered for it (change orgs via the page selector). */
  organization: Organization;
  /** Currently loaded rows of the selected org — drives the pre-submit "already registered" hint. */
  existingAccounts: DepositAccountRow[];
  /** Fired after a 201 that belongs to THIS org (created or already existed) so the page refreshes. */
  onRegistered: (outcome: Exclude<RegisterDepositAccountOutcome, "owned_by_other_org">) => void;
}

const buildSchema = (digitCount: number, accountInvalid: string) =>
  z.object({
    // Stored grouped for display; validated/submitted as raw digits.
    account: z.string().refine((value) => isValidAccount(value, digitCount), accountInvalid),
    note: z.string().max(200).optional(),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: FormValues = { account: "", note: "" };

/**
 * Registers a STATIC deposit receiving account (CLABE/CVU) for the selected organization. Business rules
 * honored here:
 *
 * - The backend register is IDEMPOTENT and looks the account up globally (no org filter): a 201 whose
 *   `organization_id` differs from the requested org means the account is already owned by ANOTHER org and
 *   nothing changed — that is surfaced as an ERROR, never as success (a deposit on it would credit the other
 *   org).
 * - Same org + already existed → informational toast, list refresh (no duplicate was created).
 * - There is no 2FA and no idempotency header on this endpoint (plain POST).
 */
export function RegisterDepositAccountDialog({
  corridor,
  open,
  onClose,
  organization,
  existingAccounts,
  onRegistered,
}: RegisterDepositAccountDialogProps) {
  const { t } = useTranslations(corridor.i18nNamespace);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = useMemo(() => buildSchema(corridor.digitCount, t("validation_account")), [corridor, t]);
  const form = useZodForm(schema, DEFAULTS);

  const typedDigits = accountDigits(form.watch("account") ?? "", corridor.digitCount);
  const alreadyInList =
    typedDigits.length === corridor.digitCount && existingAccounts.some((a) => a.account === typedDigits);

  const handleClose = () => {
    form.reset(DEFAULTS);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const digits = accountDigits(values.account, corridor.digitCount);
    const note = values.note?.trim();
    try {
      const data = await corridor.register({
        organizationId: organization.id,
        account: digits,
        ...(note ? { metadata: { note } } : {}),
      });

      // Cross-org collision: the idempotent backend returned ANOTHER org's existing mapping (201, no
      // error). Nothing was registered for this org — this must read as a failure.
      if (data.organization_id !== organization.id) {
        setSubmitError(t("error_cross_org"));
        return;
      }

      const alreadyExisted = existingAccounts.some((a) => a.id === data.id);
      if (alreadyExisted) {
        colocaToast.success(t("toast_already_registered"));
      } else {
        colocaToast.success(t("toast_registered"));
      }
      form.reset(DEFAULTS);
      onRegistered(alreadyExisted ? "already_registered" : "created");
      onClose();
    } catch (err: unknown) {
      setSubmitError(
        HttpApiService.isAccessDeniedError(err)
          ? t("error_no_write_permission")
          : HttpApiService.getErrorMessage(err) || t("error_register"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) handleClose();
      }}
      title={t("dialog_title")}
      description={t("dialog_description")}
      mobileFullscreen
    >
      <div className="space-y-4">
        {/* Target organization — fixed to the page selection so a mapping can't land on the wrong org. */}
        <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("dialog_org_label")}
          </p>
          <p className="mt-0.5 text-sm font-medium text-foreground">{organization.name}</p>
          {organization.email && <p className="text-xs text-muted-foreground">{organization.email}</p>}
        </div>

        {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}

        <ColocaForm form={form} onSubmit={handleSubmit}>
          <ColocaInputField
            name="account"
            label={t("dialog_account_label")}
            placeholder={t("dialog_account_placeholder")}
            inputMode="numeric"
            maxLength={groupedAccountMaxLength(corridor.digitCount)}
            transform={(value) => formatAccountGroups(value, corridor.digitCount)}
            description={alreadyInList ? undefined : t("dialog_account_helper")}
          />
          {alreadyInList && <p className="text-xs text-warning">{t("hint_already_registered")}</p>}
          <ColocaTextareaField
            name="note"
            label={t("dialog_note_label")}
            placeholder={t("dialog_note_placeholder")}
            rows={2}
            maxLength={200}
            count
          />
          <div className="flex justify-end gap-2 pt-2">
            <ColocaButton type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("dialog_cancel")}
            </ColocaButton>
            <ColocaButton type="submit" variant="primary" icon={Save} loading={isSubmitting}>
              {t("dialog_submit")}
            </ColocaButton>
          </div>
        </ColocaForm>
      </div>
    </ColocaDialog>
  );
}
